import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { StorageService } from './storage.service';
import { CurrencyRate } from '../models/config.model';
import { StoredItem } from '../shared/enums';

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private storageService = inject(StorageService);

  public currencies$ = new BehaviorSubject<string[]>([]);

  // The guest's currency pick per venue (restaurant id -> currency), remembered across reloads.
  // Per venue, so JOD chosen at a Jordan venue doesn't follow them to a Dubai one.
  private readonly choices = signal<Record<string, string>>(this.readStoredChoices());

  // Exchange rates, keyed by currency. Empty until setCurrencies() has loaded them.
  private readonly conversionRates = signal<ReadonlyMap<string, number>>(new Map());

  constructor(
    private apiService: ApiService,
    private dataService: DataService
  ) { }

  public get defaultCurrency(): string {
    return this.dataService.restaurant()?.currency;
  }

  // The guest's choice only applies once its rate has loaded - before that, prices would be
  // labelled e.g. "JOD" while still in the default currency's amount. A remembered currency
  // the venue no longer offers never gets a rate, so it quietly falls back to the default.
  public get currentCurrency(): string {
    const restaurantId = this.dataService.restaurant()?.id;
    const choice = restaurantId ? this.choices()[restaurantId] : undefined;
    return choice && this.conversionRates().has(choice) ? choice : this.defaultCurrency;
  }

  public set currentCurrency(currency: string) {
    this.setCurrentCurrency(currency);
  }

  public setCurrencies(): void {
    const restaurantId: string = this.dataService.restaurant().id;
    this.apiService.getCurrencies(restaurantId)
      .subscribe(response => {
        if (response.length > 0) {
          // Add default Restaurant currency to the beginning of the list
          const defaultRate: CurrencyRate = {
            currency: this.defaultCurrency,
            rate: 1
          };
          response.unshift(defaultRate);
          this.conversionRates.set(new Map(response.map((i): [string, number] => [i.currency, i.rate])));
          this.currencies$.next(Array.from(this.conversionRates().keys()));
        }
      });
  }

  public setCurrentCurrency(currency: string) {
    const restaurantId = this.dataService.restaurant()?.id;
    if (!restaurantId) return;

    this.choices.update(choices => ({ ...choices, [restaurantId]: currency }));
    this.storageService.set(StoredItem.Currency, JSON.stringify(this.choices()));
  }

  public currencyChanged(): boolean {
    return this.defaultCurrency !== this.currentCurrency;
  }

  public calculatePrice(value: number, currency: string): number {
    const rate = this.conversionRates().get(currency);
    if (rate) {
      return value / rate;
    } else {
      return value;
    }
  }

  private readStoredChoices(): Record<string, string> {
    try {
      return JSON.parse(this.storageService.get(StoredItem.Currency) ?? '{}');
    } catch {
      return {};
    }
  }
}
